import React, { createContext, useContext, useState, useEffect } from 'react';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    urgent_care_enabled: true,
    registration_enabled: true,
    online_ordering_enabled: true,
    online_appointments_enabled: true,
    services_enabled: true,
    boarding_enabled: true,
    records_enabled: true,
    hr_enabled: true,
    ai_costs_enabled: true,
    system_settings_enabled: true,
    loading: true
  });

  // Use the same backend URL logic as AuthContext
  const getBackendUrl = () => {
    const origin = window.location.origin;
    
    // If REACT_APP_BACKEND_URL is defined, use it
    if (process.env.REACT_APP_BACKEND_URL) {
      return process.env.REACT_APP_BACKEND_URL;
    }
    
    // For Digital Ocean deployment, append /pandvah-backend
    if (!origin.includes('localhost')) {
      return origin + '/pandvah-backend';
    }
    
    // For localhost, default to port 8000
    return 'http://localhost:8000';
  };

  const API_BASE_URL = getBackendUrl();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const url = `${API_BASE_URL}/api/cms-settings/public`;
        const response = await fetch(url);
        
        if (response.ok) {
          const data = await response.json();
          const finalUrgentCare = data.urgent_care_enabled !== false;
          const finalRegistration = data.registration_enabled !== false;
          const finalOnlineOrdering = data.online_ordering_enabled !== false;
          const finalOnlineAppointments = data.online_appointments_enabled !== false;
          const finalServices = data.services_enabled !== false;
          const finalBoarding = data.boarding_enabled !== false;
          const finalRecords = data.records_enabled !== false;
          const finalHR = data.hr_enabled !== false;
          const finalAICosts = data.ai_costs_enabled !== false;
          const finalSystemSettings = data.system_settings_enabled !== false;
          
          setSettings({
            urgent_care_enabled: finalUrgentCare,
            registration_enabled: finalRegistration,
            online_ordering_enabled: finalOnlineOrdering,
            online_appointments_enabled: finalOnlineAppointments,
            services_enabled: finalServices,
            boarding_enabled: finalBoarding,
            records_enabled: finalRecords,
            hr_enabled: finalHR,
            ai_costs_enabled: finalAICosts,
            system_settings_enabled: finalSystemSettings,
            loading: false
          });
        } else {
          // Default to true if can't fetch
          setSettings({
            urgent_care_enabled: true,
            registration_enabled: true,
            online_ordering_enabled: true,
            online_appointments_enabled: true,
            services_enabled: true,
            boarding_enabled: true,
            records_enabled: true,
            hr_enabled: true,
            ai_costs_enabled: true,
            system_settings_enabled: true,
            loading: false
          });
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        // Default to true on error
        setSettings({
          urgent_care_enabled: true,
          registration_enabled: true,
          online_ordering_enabled: true,
          online_appointments_enabled: true,
          services_enabled: true,
          boarding_enabled: true,
          records_enabled: true,
          hr_enabled: true,
          ai_costs_enabled: true,
          system_settings_enabled: true,
          loading: false
        });
      }
    };

    fetchSettings();
  }, [API_BASE_URL]);

  return (
    <SettingsContext.Provider value={settings}>
      {children}
    </SettingsContext.Provider>
  );
};
